import { Button, Form, notification, Popconfirm, Radio } from "antd";
import React, { useEffect, useState } from "react";
import { MultiLanguageInput } from "../../../Components/MultiLanguageInput";
import { PiPlus } from "react-icons/pi";
import { useParams } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { editQuestion, getQuestionByID } from "../action";
import { type_options } from "./options";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  question: {
    en: string;
    ar: string;
    fr: string;
  };
  type: "S" | "B";
}

const EditQuestion: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();

  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const fetchItem = async () => {
    setIsLoading(true);
    const ft = await getQuestionByID(id!);
    form.setFieldsValue({
      question: {
        en: ft?.Question.En,
        ar: ft?.Question.Ar,
        fr: ft?.Question.Fr,
      },
      type: ft?.Type,
    });
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    try {
      const formValues = await form.validateFields();
      setIsLoading(true);

      const data = await editQuestion(
        id!,
        formValues.question,
        formValues.type
      );
      if (data) {
        fetchData();
        api.success({
          message: "Question edited successfully",
        });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to edit question",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchItem();
  }, [id]);

  return (
    <Form className="space-y-5" form={form} layout="vertical">
      {contextHolder}

      <MultiLanguageInput name="question" label="Question" />
      <Form.Item name="type" label="Type" rules={[{ required: true }]}>
        <Radio.Group
          options={type_options}
          optionType="button"
          buttonStyle="solid"
        />
      </Form.Item>

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to add this question?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<FaEdit />}
              loading={isLoading}
              type="primary"
              className="w-full"
              disabled={
                !form.isFieldsTouched(true) ||
                form.getFieldsError().some(({ errors }) => errors.length > 0)
              }
            >
              {isLoading ? "Editing Question..." : "Edit Question"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditQuestion;
